const DELIVERY_TIME_SLOTS = [
  { startHour: 9, endHour: 12, value: '09:00 – 12:00', label: '09:00 – 12:00' },
  { startHour: 12, endHour: 16, value: '12:00 – 16:00', label: '12:00 – 16:00' },
  { startHour: 16, endHour: 19, value: '16:00 – 19:00', label: '16:00 – 19:00' },
  { startHour: 19, endHour: 22, value: '19:00 – 22:00', label: '19:00 – 22:00' },
];

/** Slot, bitiş saatinden kaç saat önce görünmez olur */
const SLOT_HIDE_BEFORE_END_HOURS = 1;

class DeliveryScheduler extends HTMLElement {
  /** @type {{ name: string, neighborhoods: string[] | null }[]} */
  #areas = [];

  /** @type {{ district: string, neighborhood: string | null } | null} */
  #selectedLocation = null;

  /** @type {'districts' | 'neighborhoods' | 'search'} */
  #locationView = 'districts';

  /** @type {string} */
  #activeDistrict = '';

  /** @type {Record<number, number>} */
  #feeVariantsByPrice = {};

  /** @type {boolean} */
  #enableDistanceFee = true;

  /**
   * @returns {{ id: number, quantity: number, properties: Record<string, string> }[]}
   */
  getAdditionalCartItems() {
    const feeAmount = this.#getSelectedFeeAmount();
    if (!this.#enableDistanceFee || feeAmount <= 0) {
      return [];
    }

    const feeVariantId = this.#getFeeVariantId(feeAmount);
    if (!feeVariantId) {
      return [];
    }

    const locationLabel = this.locationProperty?.value || '';
    const feePropertyLabel =
      this.distanceFeeProperty?.name.match(/^properties\[(.+)\]$/)?.[1] || 'Uzun Mesafe Ücreti';

    return [
      {
        id: feeVariantId,
        quantity: 1,
        properties: {
          [feePropertyLabel]: `${feeAmount} TL`,
          'Gönderim Bölgesi': locationLabel,
          'Ücret Tipi': 'Uzun Mesafe Teslimat',
        },
      },
    ];
  }

  /** @returns {Promise<boolean>} */
  async validateBeforeAdd() {
    if (!this.#validate()) {
      return false;
    }

    await window.DeliveryCheckoutSync?.syncFromProductForm(this.formId);

    const feeAmount = this.#getSelectedFeeAmount();
    if (!this.#enableDistanceFee || feeAmount <= 0) {
      return true;
    }

    if (Object.keys(this.#feeVariantsByPrice).length === 0) {
      await this.#loadFeeVariants();
    }

    if (!this.#getFeeVariantId(feeAmount)) {
      /** @type {Record<number, number>} */
      const map = { ...this.#feeVariantsByPrice };
      await this.#fetchFeeVariantsIntoMap(map);
      this.#feeVariantsByPrice = map;
    }

    if (!this.#getFeeVariantId(feeAmount)) {
      this.#showError(
        `${feeAmount} TL uzun mesafe ücreti için varyant bulunamadı. Admin → Ürünler → "Uzun Mesafe Teslimat Ücreti" ürününün Online Store kanalında yayınlandığından ve ${feeAmount} TL fiyatlı varyantın olduğundan emin olun.`
      );
      return false;
    }

    return true;
  }

  connectedCallback() {
    this.formId = this.dataset.formId;
    this.cutoffHour = Number(this.dataset.cutoffHour ?? 13);
    this.cutoffMinute = Number(this.dataset.cutoffMinute ?? 0);
    this.slotStartHour = Number(this.dataset.slotStartHour ?? 9);
    this.slotEndHour = Number(this.dataset.slotEndHour ?? 18);
    this.maxAdvanceDays = Number(this.dataset.maxAdvanceDays ?? 30);
    this.minLeadHours = Number(this.dataset.minLeadHours ?? 2);
    this.locationPlaceholder = this.dataset.locationPlaceholder ?? 'Gönderim Bölgesi Seçiniz';
    this.#enableDistanceFee = this.dataset.enableDistanceFee === 'true';

    this.#areas = window.KAYSERI_DELIVERY_AREAS ?? [];

    this.dayTypeInputs = this.querySelectorAll('[data-day-type]');
    this.dateField = this.querySelector('[data-date-field]');
    this.dateInput = this.querySelector('[data-date-input]');
    this.timeSelect = this.querySelector('[data-time-select]');
    this.dayProperty = this.querySelector('[data-day-property]');
    this.timeProperty = this.querySelector('[data-time-property]');
    this.locationProperty = this.querySelector('[data-location-property]');
    this.districtProperty = this.querySelector('[data-district-property]');
    this.distanceFeeProperty = this.querySelector('[data-distance-fee-property]');
    this.feeNotice = this.querySelector('[data-fee-notice]');
    this.todayOption = this.querySelector('[data-today-option]');
    this.errorEl = this.querySelector('[data-error]');

    this.locationTrigger = this.querySelector('[data-location-trigger]');
    this.locationPanel = this.querySelector('[data-location-panel]');
    this.locationSearch = this.querySelector('[data-location-search]');
    this.locationDistrictsView = this.querySelector('[data-location-districts]');
    this.locationNeighborhoodsView = this.querySelector('[data-location-neighborhoods]');
    this.locationSearchView = this.querySelector('[data-location-search-results]');
    this.locationDistrictList = this.querySelector('[data-district-list]');
    this.locationNeighborhoodList = this.querySelector('[data-neighborhood-list]');
    this.locationSearchList = this.querySelector('[data-search-list]');
    this.locationActiveDistrict = this.querySelector('[data-active-district]');
    this.locationBackBtn = this.querySelector('[data-location-back]');
    this.locationCloseBtn = this.querySelector('[data-location-close]');

    this.#renderDistrictList();
    this.#bindEvents();
    this.#refresh();
    this.#loadFeeVariants().then(() => {
      if (this.#selectedLocation) {
        this.#updateFeeDisplay();
      }
    });
    this.interval = window.setInterval(() => this.#refreshTodayAvailability(), 60000);
  }

  /** @param {string | null | undefined} text */
  #parseDistanceFee(text) {
    if (!text) return 0;
    const match = text.match(/\+\s*(\d+)\s*TL/i);
    return match ? Number(match[1]) : 0;
  }

  /**
   * Mahalle listesindeki tutarlarla eşlemek için anahtar üretir.
   * Önce varyant adı/seçenek (örn. "250") kullanılır — satış fiyatı zamlanınca bozulmaz.
   * @param {{ price: number, title?: string, public_title?: string, option1?: string | null, option2?: string | null, option3?: string | null }} variant
   * @returns {number}
   */
  #extractVariantAmount(variant) {
    const parts = [variant.option1, variant.option2, variant.option3, variant.public_title, variant.title].filter(
      Boolean
    );

    for (const part of parts) {
      const exact = String(part).trim().match(/^(\d{2,4})(?:\s*TL)?$/i);
      if (exact) {
        const amount = Number(exact[1]);
        if (amount >= 50 && amount <= 2000) {
          return amount;
        }
      }

      const loose = String(part).match(/(\d{2,4})\s*TL/i);
      if (loose) {
        const amount = Number(loose[1]);
        if (amount >= 50 && amount <= 2000) {
          return amount;
        }
      }
    }

    const fromPrice = Math.round(Number(variant.price) / 100);
    return fromPrice > 0 ? fromPrice : 0;
  }

  /** @param {Record<number, number>} map */
  #registerFeeVariant(map, amount, variantId) {
    const price = Number(amount);
    const id = Number(variantId);
    if (price > 0 && id > 0) {
      map[price] = id;
    }
  }

  /** @returns {number} */
  #getSelectedFeeAmount() {
    const fromSelection = this.#selectedLocation?.neighborhood || this.#selectedLocation?.district || '';
    const fromProperty = this.locationProperty?.value || '';
    return this.#parseDistanceFee(fromSelection) || this.#parseDistanceFee(fromProperty);
  }

  async #loadFeeVariants() {
    if (!this.#enableDistanceFee) return;

    /** @type {Record<number, number>} */
    const map = {};

    const embeddedMap = this.querySelector('script[data-delivery-fee-map]');
    if (embeddedMap?.textContent?.trim()) {
      try {
        const parsed = JSON.parse(embeddedMap.textContent);
        Object.entries(parsed).forEach(([amount, variantId]) => {
          this.#registerFeeVariant(map, amount, variantId);
        });
      } catch (_error) {
        // ignore invalid JSON
      }
    }

    const manualMap = this.dataset.feeVariantMap;
    if (manualMap && manualMap !== '{}') {
      try {
        const parsed = JSON.parse(manualMap);
        Object.entries(parsed).forEach(([amount, variantId]) => {
          this.#registerFeeVariant(map, amount, variantId);
        });
      } catch (_error) {
        // ignore invalid JSON
      }
    }

    // Always merge storefront product.js so option titles stay the source of truth
    // even if the Liquid-embedded map was built from outdated prices.
    await this.#fetchFeeVariantsIntoMap(map);

    this.#feeVariantsByPrice = map;
  }

  /** @param {Record<number, number>} map */
  async #fetchFeeVariantsIntoMap(map) {
    const handle = this.dataset.feeProductHandle?.trim();
    if (!handle) return;

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return;

      const product = await response.json();
      product.variants?.forEach((variant) => {
        const amount = this.#extractVariantAmount(variant);
        if (amount > 0) {
          map[amount] = variant.id;
        }
      });
    } catch (_error) {
      // product fetch failed
    }
  }

  /** Sepete eklemeden hemen önce varyant haritasının hazır olduğundan emin olur. */
  async prepareForAddToCart() {
    if (!this.#enableDistanceFee) return;

    if (Object.keys(this.#feeVariantsByPrice).length === 0) {
      await this.#loadFeeVariants();
    }

    const feeAmount = this.#getSelectedFeeAmount();
    if (feeAmount > 0 && !this.#getFeeVariantId(feeAmount)) {
      /** @type {Record<number, number>} */
      const map = { ...this.#feeVariantsByPrice };
      await this.#fetchFeeVariantsIntoMap(map);
      this.#feeVariantsByPrice = map;
    }
  }

  /** @param {number} amount */
  #getFeeVariantId(amount) {
    return this.#feeVariantsByPrice[amount] ?? null;
  }

  #updateFeeDisplay() {
    const amount = this.#getSelectedFeeAmount();

    if (this.distanceFeeProperty) {
      this.distanceFeeProperty.value = amount > 0 ? `${amount} TL` : '';
    }

    if (!this.feeNotice) return;

    if (!this.#enableDistanceFee || amount <= 0) {
      this.feeNotice.hidden = true;
      this.feeNotice.textContent = '';
      return;
    }

    const variantId = this.#getFeeVariantId(amount);

    if (variantId) {
      this.feeNotice.hidden = false;
      this.feeNotice.textContent = `Seçilen bölge için +${amount} TL uzun mesafe ücreti sepete eklenecek.`;
    } else if (this.dataset.feeProductFound === 'false') {
      this.feeNotice.hidden = false;
      this.feeNotice.textContent = `Seçilen bölge için +${amount} TL uzun mesafe ücreti geçerli. "Uzun Mesafe Teslimat Ücreti" ürünü Online Store kanalında yayınlanmalı (handle: ${this.dataset.feeProductHandle || 'uzun-mesafe-teslimat-ucreti'}).`;
    } else {
      this.feeNotice.hidden = false;
      this.feeNotice.textContent = `Seçilen bölge için +${amount} TL uzun mesafe ücreti geçerli. ${amount} TL varyantı bulunamadı — ürün fiyatlarını kontrol edin.`;
    }
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.hidden = false;
    this.errorEl.textContent = message;
  }

  #clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
  }

  /** @returns {Date} */
  #nowInIstanbul() {
    const iso = new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Istanbul' });
    return new Date(iso.replace(' ', 'T'));
  }

  /** @param {Date} date */
  #dateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** @param {string} key */
  #parseDateKey(key) {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  /** @param {Date} date */
  #formatDisplayDate(date) {
    return date.toLocaleDateString('tr-TR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  /** @returns {boolean} */
  #isSameDayCutoffPassed() {
    const now = this.#nowInIstanbul();
    const cutoff = new Date(now);
    cutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);
    return now.getTime() >= cutoff.getTime();
  }

  /** @returns {Date} */
  #todayDate() {
    return this.#nowInIstanbul();
  }

  /** @returns {Date} */
  #tomorrowDate() {
    const date = this.#todayDate();
    date.setDate(date.getDate() + 1);
    return date;
  }

  /** @returns {string} */
  #selectedDayType() {
    const checked = this.querySelector('[data-day-type]:checked');
    return checked?.value ?? 'tomorrow';
  }

  /** @returns {Date} */
  #selectedDate() {
    const type = this.#selectedDayType();

    if (type === 'today') {
      return this.#todayDate();
    }

    if (type === 'tomorrow') {
      return this.#tomorrowDate();
    }

    if (this.dateInput?.value) {
      return this.#parseDateKey(this.dateInput.value);
    }

    return this.#tomorrowDate();
  }

  /** @param {Date} date */
  #buildTimeSlots(date) {
    const now = this.#nowInIstanbul();
    const isToday = this.#dateKey(date) === this.#dateKey(now);
    /** @type {{ value: string, label: string }[]} */
    const slots = [];

    DELIVERY_TIME_SLOTS.forEach(({ endHour, value, label }) => {
      if (isToday) {
        // Örn. 12:00–16:00 dilimi, 15:00'a kadar görünür (bitişten 1 saat önce).
        const hideAt = new Date(date);
        hideAt.setHours(endHour - SLOT_HIDE_BEFORE_END_HOURS, 0, 0, 0);

        if (now.getTime() >= hideAt.getTime()) {
          return;
        }
      }

      slots.push({ value, label });
    });

    return slots;
  }

  /** @param {string} district @param {string | null} neighborhood */
  #formatLocationLabel(district, neighborhood) {
    if (neighborhood) {
      return `${district} · ${neighborhood}`;
    }
    return district;
  }

  #renderDistrictList() {
    if (!this.locationDistrictList) return;

    this.locationDistrictList.innerHTML = '';

    this.#areas.forEach((area) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'delivery-scheduler__location-item';
      button.textContent = area.name;
      button.addEventListener('click', () => this.#selectDistrict(area.name));
      li.appendChild(button);
      this.locationDistrictList.appendChild(li);
    });
  }

  /** @param {string} districtName */
  #selectDistrict(districtName) {
    const area = this.#areas.find((item) => item.name === districtName);
    if (!area) return;

    if (area.neighborhoods === null || area.neighborhoods.length === 0) {
      this.#setLocation(districtName, null);
      this.#closeLocationPanel();
      return;
    }

    this.#activeDistrict = districtName;
    this.#locationView = 'neighborhoods';
    this.#renderNeighborhoodList(area.neighborhoods);
    this.#updateLocationViews();
  }

  /** @param {string[]} neighborhoods */
  #renderNeighborhoodList(neighborhoods) {
    if (!this.locationNeighborhoodList || !this.locationActiveDistrict) return;

    this.locationActiveDistrict.textContent = this.#activeDistrict || '';
    this.locationNeighborhoodList.innerHTML = '';

    neighborhoods.forEach((name) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'delivery-scheduler__location-item';
      button.textContent = name;
      button.addEventListener('click', () => {
        this.#setLocation(this.#activeDistrict, name);
        this.#closeLocationPanel();
      });
      li.appendChild(button);
      this.locationNeighborhoodList.appendChild(li);
    });
  }

  /** @param {string} query */
  #runLocationSearch(query) {
    const normalized = query.trim().toLocaleLowerCase('tr-TR');
    if (!normalized) {
      this.#locationView = 'districts';
      this.#updateLocationViews();
      return;
    }

    /** @type {{ district: string, neighborhood: string | null, label: string }[]} */
    const results = [];

    this.#areas.forEach((area) => {
      const districtMatch = area.name.toLocaleLowerCase('tr-TR').includes(normalized);
      const hasNeighborhoods = Array.isArray(area.neighborhoods) && area.neighborhoods.length > 0;

      if (districtMatch) {
        results.push({
          district: area.name,
          neighborhood: null,
          label: hasNeighborhoods ? `${area.name} (ilçe)` : area.name,
        });
      }

      if (Array.isArray(area.neighborhoods)) {
        area.neighborhoods.forEach((neighborhood) => {
          if (neighborhood.toLocaleLowerCase('tr-TR').includes(normalized)) {
            results.push({
              district: area.name,
              neighborhood,
              label: `${area.name} · ${neighborhood}`,
            });
          }
        });
      }
    });

    this.#locationView = 'search';
    this.#renderSearchResults(results);
    this.#updateLocationViews();
  }

  /** @param {{ district: string, neighborhood: string | null, label: string }[]} results */
  #renderSearchResults(results) {
    if (!this.locationSearchList) return;

    this.locationSearchList.innerHTML = '';

    if (results.length === 0) {
      const li = document.createElement('li');
      li.className = 'delivery-scheduler__location-empty';
      li.textContent = 'Sonuç bulunamadı';
      this.locationSearchList.appendChild(li);
      return;
    }

    results.forEach((result) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'delivery-scheduler__location-item';
      button.textContent = result.label;
      button.addEventListener('click', () => {
        if (!result.neighborhood) {
          const area = this.#areas.find((item) => item.name === result.district);
          if (area?.neighborhoods?.length) {
            this.#selectDistrict(result.district);
            if (this.locationSearch instanceof HTMLInputElement) {
              this.locationSearch.value = '';
            }
            return;
          }
        }

        this.#setLocation(result.district, result.neighborhood);
        this.#closeLocationPanel();
      });
      li.appendChild(button);
      this.locationSearchList.appendChild(li);
    });
  }

  /** @param {string} district @param {string | null} neighborhood */
  #setLocation(district, neighborhood) {
    this.#selectedLocation = { district, neighborhood };
    const label = this.#formatLocationLabel(district, neighborhood);

    if (this.locationTrigger) {
      this.locationTrigger.textContent = label;
      this.locationTrigger.classList.add('delivery-scheduler__location-trigger--selected');
    }

    if (this.districtProperty) {
      this.districtProperty.value = district;
    }

    if (this.locationProperty) {
      this.locationProperty.value = neighborhood || district;
    }

    this.#updateFeeDisplay();

    if (this.errorEl) {
      this.errorEl.hidden = true;
    }
  }

  #updateLocationViews() {
    const isSearch = this.#locationView === 'search';
    const isNeighborhoods = this.#locationView === 'neighborhoods';

    if (this.locationDistrictsView) {
      this.locationDistrictsView.hidden = isSearch || isNeighborhoods;
    }

    if (this.locationNeighborhoodsView) {
      this.locationNeighborhoodsView.hidden = !isNeighborhoods;
    }

    if (this.locationSearchView) {
      this.locationSearchView.hidden = !isSearch;
    }

    if (this.locationBackBtn instanceof HTMLButtonElement) {
      this.locationBackBtn.hidden = !isNeighborhoods;
    }
  }

  #openLocationPanel() {
    if (!this.locationPanel) return;

    this.locationPanel.hidden = false;
    this.locationTrigger?.setAttribute('aria-expanded', 'true');
    this.#locationView = 'districts';

    this.#updateLocationViews();

    if (this.locationSearch instanceof HTMLInputElement) {
      this.locationSearch.value = '';
    }

    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleDocumentKeydown);
  }

  #closeLocationPanel() {
    if (!this.locationPanel) return;

    this.locationPanel.hidden = true;
    this.locationTrigger?.setAttribute('aria-expanded', 'false');
    this.#locationView = 'districts';
    this.#updateLocationViews();

    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#closeLocationPanel();
  };

  /** @param {KeyboardEvent} event */
  #handleDocumentKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closeLocationPanel();
    }
  };

  #bindEvents() {
    this.dayTypeInputs.forEach((input) => {
      input.addEventListener('change', () => this.#refresh());
    });

    this.dateInput?.addEventListener('change', () => this.#refresh());
    this.timeSelect?.addEventListener('change', () => this.#updateProperties());

    this.locationTrigger?.addEventListener('click', () => {
      const isOpen = this.locationPanel && !this.locationPanel.hidden;
      if (isOpen) {
        this.#closeLocationPanel();
      } else {
        this.#openLocationPanel();
      }
    });

    this.locationCloseBtn?.addEventListener('click', () => this.#closeLocationPanel());

    this.locationBackBtn?.addEventListener('click', () => {
      this.#locationView = 'districts';
      if (this.locationSearch instanceof HTMLInputElement) {
        this.locationSearch.value = '';
      }
      this.#updateLocationViews();
    });

    this.locationSearch?.addEventListener('input', () => {
      if (this.locationSearch instanceof HTMLInputElement) {
        this.#runLocationSearch(this.locationSearch.value);
      }
    });
  }

  #refreshTodayAvailability() {
    const cutoffPassed = this.#isSameDayCutoffPassed();

    if (this.todayOption) {
      this.todayOption.disabled = cutoffPassed;
      if (cutoffPassed) {
        this.todayOption.closest('label')?.classList.add('delivery-scheduler__option--disabled');
      } else {
        this.todayOption.closest('label')?.classList.remove('delivery-scheduler__option--disabled');
      }
    }

    if (cutoffPassed && this.#selectedDayType() === 'today') {
      const fallback = this.querySelector('[data-day-type][value="tomorrow"]');
      if (fallback instanceof HTMLInputElement) {
        fallback.checked = true;
      }
    }
  }

  #refresh() {
    this.#refreshTodayAvailability();

    const type = this.#selectedDayType();
    const today = this.#todayDate();

    if (this.dateField) {
      this.dateField.hidden = type !== 'custom';
    }

    if (this.dateInput) {
      const minDate = this.#isSameDayCutoffPassed() ? this.#tomorrowDate() : today;
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + this.maxAdvanceDays);

      this.dateInput.min = this.#dateKey(minDate);
      this.dateInput.max = this.#dateKey(maxDate);

      if (type === 'custom' && !this.dateInput.value) {
        this.dateInput.value = this.#dateKey(minDate);
      }
    }

    const selectedDate = this.#selectedDate();
    const slots = this.#buildTimeSlots(selectedDate);
    const previousValue = this.timeSelect?.value;

    if (this.timeSelect) {
      this.timeSelect.innerHTML = '';

      if (slots.length === 0) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = 'Uygun saat yok';
        this.timeSelect.appendChild(option);
        this.timeSelect.disabled = true;
      } else {
        this.timeSelect.disabled = false;

        slots.forEach((slot) => {
          const option = document.createElement('option');
          option.value = slot.value;
          option.textContent = slot.label;
          this.timeSelect.appendChild(option);
        });

        const stillValid = slots.some((slot) => slot.value === previousValue);
        this.timeSelect.value = stillValid ? previousValue : slots[0].value;
      }
    }

    this.#updateProperties();
  }

  #updateProperties() {
    const type = this.#selectedDayType();
    const selectedDate = this.#selectedDate();
    const formattedDate = this.#formatDisplayDate(selectedDate);
    const time = this.timeSelect?.value ?? '';

    let dayLabel = formattedDate;

    if (type === 'today') {
      dayLabel = `Bugün (${formattedDate})`;
    } else if (type === 'tomorrow') {
      dayLabel = `Yarın (${formattedDate})`;
    }

    if (this.dayProperty) {
      this.dayProperty.value = dayLabel;
    }

    if (this.timeProperty) {
      this.timeProperty.value = time;
    }

    if (this.errorEl) {
      this.errorEl.hidden = true;
    }
  }

  /** @returns {boolean} */
  #validate() {
    if (!this.#selectedLocation?.district) {
      this.#showError('Lütfen ilçe seçin.');
      this.#openLocationPanel();
      return false;
    }

    const area = this.#areas.find((item) => item.name === this.#selectedLocation?.district);
    if (
      area &&
      Array.isArray(area.neighborhoods) &&
      area.neighborhoods.length > 0 &&
      !this.#selectedLocation?.neighborhood
    ) {
      this.#showError('Lütfen mahalle seçin.');
      this.#openLocationPanel();
      return false;
    }

    const time = this.timeSelect?.value;

    if (!time) {
      this.#showError('Lütfen teslimat saati seçin.');
      return false;
    }

    if (this.dayProperty && !this.dayProperty.value) {
      this.#showError('Lütfen teslimat günü seçin.');
      return false;
    }

    this.#clearError();
    return true;
  }
}

if (!customElements.get('delivery-scheduler')) {
  customElements.define('delivery-scheduler', DeliveryScheduler);
}
